import { useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBook,
  faBug,
  faCog,
  faFileMagnifyingGlass,
  faGauge,
  faKey,
  faLockKeyhole,
  faPlug,
  faRightToBracket,
  faSparkles,
  faUserCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { ApiKeyOverview } from "src/apiKeys/ApiKeysOverview";
import { AuditLogsPage } from "src/auditLogs/AuditLogsPage";
import { SimpleRoleType } from "src/clients/taktile-api";
import { ConnectionOverview } from "src/connections/ConnectionOverview";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { DashboardContent } from "src/layout/DashboardContent";
import { OrgLogoModal } from "src/layout/OrgLogoModal/OrgLogoModal";
import { PageHeader } from "src/layout/PageHeader";
import { WorkspaceFormModal } from "src/layout/WorkspaceFormModal/WorkspaceFormModal";
import { MainSideBar } from "src/mainPage/MainSideBar";
import { DebugSidePane } from "src/powertools/DebugPane";
import { SessionsPage } from "src/powertools/SessionsPage";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { DashboardPageParams, getUrlToSettingsPage } from "src/router/urls";
import { ScimTokenModal } from "src/scimToken/ScimTokenModal";
import { AgentCredentialsOverview } from "src/settings/AgentCredentialsOverview";
import { AiUsageLimitsPage } from "src/settings/AiUsageLimitsPage";
import { UsersPermissionsPage } from "src/userManagement/UsersPermissionsPage";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";
import { useParamsDecode } from "src/utils/useParamsDecode";

const NavItem: React.FC<{
  selected: boolean;
  page: SettingsSubpages;
  icon: IconProp;
  title: string;
}> = ({ selected, page, icon, title }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  return (
    <Link
      className={twJoin(linkClassName, selected && "bg-gray-50")}
      to={getUrlToSettingsPage(orgId, wsId, page, {
        keepFlags: true,
      })}
    >
      <Icon icon={icon} size="xs" />
      <p className="font-inter-medium-13px text-gray-800">{title}</p>
    </Link>
  );
};

export enum SettingsSubpages {
  Connections = "connections",
  ApiKeys = "api-keys",
  AgentCredentials = "agent-credentials",
  AiUsageLimits = "ai-usage-limits",
  Users = "users",
  AuditLogs = "audit-logs",
  Powertools = "powertools",
  Sessions = "sessions",
}

type SettingsNavBarProps = {
  currentSubpage: SettingsSubpages;
  setIsWsModalOpen: (open: boolean) => void;
  setIsOrgModalOpen: (open: boolean) => void;
  openScimModal: () => void;
};

const linkClassName =
  "flex flex-row items-center gap-x-2 rounded-lg px-2 py-1.5 hover:bg-gray-100";

const SettingsNavBar: React.FC<SettingsNavBarProps> = ({
  currentSubpage,
  setIsWsModalOpen,
  setIsOrgModalOpen,
  openScimModal,
}) => {
  const { org, workspace } = useWorkspaceAndOrg();
  const capabilities = useCapabilities();

  const isPowerToolsEnabled = isFeatureFlagEnabled(FEATURE_FLAGS.powertools);
  const { role } = useCapabilities();

  return (
    <div className="flex h-full w-52 shrink-0 flex-col gap-y-2 bg-white px-2.5 py-4 shadow-base">
      {capabilities.connections.canAccess && (
        <NavItem
          icon={faPlug}
          page={SettingsSubpages.Connections}
          selected={SettingsSubpages.Connections === currentSubpage}
          title="Connections"
        />
      )}
      {capabilities.apiKeys.canAccess &&
        isFeatureFlagEnabled(FEATURE_FLAGS.byokLlmCredentials) && (
          <NavItem
            icon={faSparkles}
            page={SettingsSubpages.AgentCredentials}
            selected={SettingsSubpages.AgentCredentials === currentSubpage}
            title="AI credentials"
          />
        )}
      {capabilities.aiUsageLimits.canAccess && (
        <NavItem
          icon={faGauge}
          page={SettingsSubpages.AiUsageLimits}
          selected={SettingsSubpages.AiUsageLimits === currentSubpage}
          title="AI usage limits"
        />
      )}
      {capabilities.apiKeys.canAccess && (
        <NavItem
          icon={faKey}
          page={SettingsSubpages.ApiKeys}
          selected={SettingsSubpages.ApiKeys === currentSubpage}
          title="API keys"
        />
      )}
      {org?.whitelabel && capabilities.organization.canEditLogo && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => setIsOrgModalOpen(true)}
        >
          <Icon icon={faCog} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            Organization settings
          </span>
        </button>
      )}
      {capabilities.usersPermissions.canAccess && (
        <NavItem
          icon={faUserCircle}
          page={SettingsSubpages.Users}
          selected={SettingsSubpages.Users === currentSubpage}
          title="User permissions"
        />
      )}
      {capabilities.scimToken.canAccess && (
        <button
          className={linkClassName}
          data-loc="scim-token-nav"
          type="button"
          onClick={openScimModal}
        >
          <Icon icon={faLockKeyhole} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            SCIM token
          </span>
        </button>
      )}
      {capabilities.auditLogs.canAccess && (
        <NavItem
          icon={faFileMagnifyingGlass}
          page={SettingsSubpages.AuditLogs}
          selected={SettingsSubpages.AuditLogs === currentSubpage}
          title="Audit log"
        />
      )}
      {capabilities.workspaces.canEdit && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => setIsWsModalOpen(true)}
        >
          <Icon icon={faCog} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            Workspace settings
          </span>
        </button>
      )}
      {capabilities.apiDocs.canAccess && workspace?.base_url && (
        <button
          className={linkClassName}
          type="button"
          onClick={() => {
            window.open(`https://${workspace.base_url}/public/docs`, "_blank");
          }}
        >
          <Icon icon={faBook} size="xs" />
          <span className="font-inter-medium-13px text-gray-800">
            API documentation
          </span>
        </button>
      )}
      {role === SimpleRoleType.ADMIN && isPowerToolsEnabled && (
        <>
          <NavItem
            icon={faBug}
            page={SettingsSubpages.Powertools}
            selected={SettingsSubpages.Powertools === currentSubpage}
            title="Debug"
          />
          <NavItem
            icon={faRightToBracket}
            page={SettingsSubpages.Sessions}
            selected={SettingsSubpages.Sessions === currentSubpage}
            title="Sessions"
          />
        </>
      )}
    </div>
  );
};

export const SettingsPage: React.FC<{ subpage: SettingsSubpages }> = ({
  subpage: currrentSubpage,
}) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const [isWsModalOpen, setIsWsModalOpen] = useState(false);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const {
    isOpen: isScimModalOpen,
    openModal: openScimModal,
    closeModal: closeScimModal,
  } = useModal();

  useDocumentTitle(["Settings"]);

  const renderPage = () => {
    switch (currrentSubpage) {
      case SettingsSubpages.Connections:
        return <ConnectionOverview workspaceId={wsId} />;
      case SettingsSubpages.ApiKeys:
        return <ApiKeyOverview organizationId={orgId} workspaceId={wsId} />;
      case SettingsSubpages.AgentCredentials:
        return <AgentCredentialsOverview />;
      case SettingsSubpages.AiUsageLimits:
        return <AiUsageLimitsPage workspaceId={wsId} />;
      case SettingsSubpages.Users:
        return <UsersPermissionsPage />;
      case SettingsSubpages.AuditLogs:
        return <AuditLogsPage />;
      case SettingsSubpages.Powertools:
        return <DebugSidePane />;
      case SettingsSubpages.Sessions:
        return <SessionsPage />;
    }
  };

  return (
    <DashboardContent sidebar={<MainSideBar />} removePadding>
      <PageHeader title="Settings" />
      <div className="flex h-full min-h-0 flex-grow flex-row border-gray-100 border-l">
        <SettingsNavBar
          currentSubpage={currrentSubpage}
          openScimModal={openScimModal}
          setIsOrgModalOpen={setIsOrgModalOpen}
          setIsWsModalOpen={setIsWsModalOpen}
        />
        <div className="decideScrollbar flex h-full flex-grow flex-col overflow-auto">
          {renderPage()}
        </div>
      </div>
      <WorkspaceFormModal
        isOpen={isWsModalOpen}
        orgId={orgId}
        variant="Edit"
        wsId={wsId}
        onClose={() => setIsWsModalOpen(false)}
      />
      <OrgLogoModal
        isOpen={isOrgModalOpen}
        onClose={() => setIsOrgModalOpen(false)}
      />
      <ScimTokenModal
        open={isScimModalOpen}
        orgId={orgId}
        onClose={closeScimModal}
      />
    </DashboardContent>
  );
};
